import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { t, getLocale } from "../lib/i18n";
import LangSwitcher from "./components/LangSwitcher";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, CalendarBlank, Clock, ArrowLeft } from "@phosphor-icons/react";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function AppointmentToken() {
  const { token } = useParams();
  const location = useLocation();
  const [locale, setLocaleState] = useState(getLocale());
  const tr = (k, params) => t(k, locale, params);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // "confirmed" | "cancelled"
  const [autoDone, setAutoDone] = useState(false);

  const load = async () => {
    try {
      const { data } = await pub.get(`/public/appointment/${token}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const doAction = async (which) => {
    setBusy(true);
    try {
      await pub.post(`/public/appointment/${token}/${which}`);
      setAction(which === "confirm" ? "confirmed" : "cancelled");
      load();
    } finally { setBusy(false); }
  };

  // Auto-trigger when URL is /cita/:token/confirmar or /cancelar
  useEffect(() => {
    if (autoDone || !data) return;
    const path = location.pathname;
    const isCancelled = data.appointment.status === "Cancelado";
    const isConfirmed = !!data.appointment.confirmed_at;
    if (path.endsWith("/confirmar") && !isConfirmed && !isCancelled) {
      doAction("confirm"); setAutoDone(true);
    } else if (path.endsWith("/cancelar") && !isCancelled) {
      doAction("cancel"); setAutoDone(true);
    }
    // eslint-disable-next-line
  }, [data, location.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">…</div>;
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center text-center p-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">{tr("enlace.invalido")}</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">404</h1>
        <p className="text-sm text-gray-600 mt-3">{tr("404.msg")}</p>
      </div>
    </div>
  );

  const a = data.appointment;
  const dt = new Date(a.date);
  const lang = locale === "en" ? "en-GB" : (locale === "ca" ? "ca-ES" : "es-ES");
  const dateStr = dt.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = dt.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" });

  const isCancelled = a.status === "Cancelado";
  const isConfirmed = !!a.confirmed_at;

  return (
    <div className="ag-light-surface min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white border border-gray-200 rounded-sm p-8" data-testid="appt-token-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[var(--ag-gold)] text-white grid place-items-center font-display font-bold rounded-sm">W</div>
              <span className="font-display font-bold text-lg">{data.org.name}</span>
            </div>
            <LangSwitcher locale={locale} onChange={setLocaleState} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-6">{tr("tu.reserva")}</p>

          <div className="mt-3 bg-gray-50 rounded-sm p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2"><CalendarBlank size={14} weight="duotone" /><b className="capitalize">{dateStr}</b></div>
            <div className="flex items-center gap-2"><Clock size={14} weight="duotone" />{timeStr} · {a.duration_min} {tr("min")}</div>
            <div className="text-xs text-gray-500 pt-1">{a.wash_type} · {tr("matricula")} <span className="font-mono-tab text-gray-900">{a.license_plate}</span></div>
            {a.car_make_model && <div className="text-xs text-gray-500">{a.car_make_model}</div>}
          </div>

          {isCancelled || action === "cancelled" ? (
            <div className="mt-6 flex flex-col items-center text-center" data-testid="state-cancelled">
              <XCircle size={40} weight="duotone" className="text-red-600" />
              <h2 className="font-display text-2xl font-bold tracking-tighter mt-3">{tr("cita.cancelada")}</h2>
              <p className="text-xs text-gray-500 mt-2">{tr("slot.liberado")}</p>
            </div>
          ) : isConfirmed || action === "confirmed" ? (
            <div className="mt-6 flex flex-col items-center text-center" data-testid="state-confirmed">
              <CheckCircle size={40} weight="duotone" className="text-emerald-600" />
              <h2 className="font-display text-2xl font-bold tracking-tighter mt-3">{tr("cita.confirmada")}</h2>
              <p className="text-xs text-gray-500 mt-2">{tr("gracias.confirmar")}</p>
              <Button variant="outline" onClick={()=>doAction("cancel")} disabled={busy}
                className="rounded-sm mt-5 text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-btn">
                {busy ? tr("cancelando") : tr("cancelar.de.todos.modos")}
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-2" data-testid="state-pending">
              <p className="text-sm text-gray-600">{tr("vas.venir")}</p>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button onClick={()=>doAction("confirm")} disabled={busy}
                  className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-11" data-testid="confirm-btn">
                  <CheckCircle className="mr-1.5" weight="fill" /> {tr("confirmar")}
                </Button>
                <Button onClick={()=>doAction("cancel")} disabled={busy} variant="outline"
                  className="rounded-sm h-11 text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-btn-2">
                  <XCircle className="mr-1.5" weight="fill" /> {tr("cancelar")}
                </Button>
              </div>
            </div>
          )}

          <a href={`/reservar/${data.org.slug}`} className="mt-8 text-xs text-gray-500 inline-flex items-center gap-1 hover:text-gray-900">
            <ArrowLeft size={11} /> {tr("reservar.otra")}
          </a>
        </div>
        <p className="mt-4 text-[10px] text-gray-500 text-center px-4 leading-relaxed">
          {data.org.name} · NIF {data.org.nif}
        </p>
      </div>
    </div>
  );
}
